import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import plur from 'plur'
import * as React from 'react'

import {useBranchesWithBuildsArg} from '../../../../../../rest/branchesWithBuilds'
import type {StatusKey} from '../../../../../../rest/schemata'
import {stringifyKey} from '../../../../../../rest/schemata'
import {restApi} from '../../../../../../services/rest'
import type {NormalizedBranchWithBuilds} from '../../../../../../types'
import {toBuildTypeId} from '../../../../../../types'
import {toGroups} from '../../../../../../utils/array'
import {emptyArray} from '../../../../../../utils/empty'
import {useBuildsClassName} from '../../../../../common/Builds/Builds'
import Details from '../../../../../common/Details/Details'
import Grid from '../../../../../common/Grid/Grid'
import Incremental from '../../../../../common/Incremental/Incremental'
import SvgIcon from '../../../../../common/SvgIcon/SvgIcon'
import Visible from '../../../../../common/Visible/Visible'

import CollapsibleBranchLine from './CollapsibleBranchLine/CollapsibleBranchLine.container'
import type {Props} from './CollapsibleBranchLines.types'
import {BranchesToShow} from './CollapsibleBranchLines.types'

import styles from './CollapsibleBranchLines.css'

const GROUP_SIZE = 10
const IMMEDIATE_RENDER_COUNT = 5

const branchHasBuilds = (branch: NormalizedBranchWithBuilds) => branch.builds.build.length > 0

export default function CollapsibleBranchLines({
  expanded,
  buildTypeId = toBuildTypeId(''),
  branchesToShow,
  onCollapse,
  onExpand,
  withTitle,
}: Props): React.ReactNode {
  const arg = useBranchesWithBuildsArg()
  const {normalizedBranches} = restApi.endpoints.getAllBranchesOfBuildTypeMerged.useQuery(arg, {
    selectFromResult: ({data}) => ({normalizedBranches: data}),
  })
  const branches = React.useMemo(() => {
    if (normalizedBranches == null) {
      return emptyArray
    }
    const dedupedKeys = Array.from(new Set(normalizedBranches.result))
    return dedupedKeys.filter(key => {
      const branch = normalizedBranches.entities.branches?.[key]
      if (branch == null) {
        return false
      }
      switch (branchesToShow) {
        case BranchesToShow.DEFAULT:
          return branch.default === true && branchHasBuilds(branch)
        case BranchesToShow.NON_DEFAULT:
          return branch.default !== true && branchHasBuilds(branch)
        case BranchesToShow.NON_BUILT:
          return !branchHasBuilds(branch)
        default:
          return false
      }
    })
  }, [branchesToShow, normalizedBranches])
  const nonBuilt = branchesToShow === BranchesToShow.NON_BUILT
  const groups = toGroups(GROUP_SIZE, branches)
  const renderWhenVisible = groups.length > IMMEDIATE_RENDER_COUNT

  const generateBranchItem = (branchKey: StatusKey, isVisible?: boolean) => (
    <CollapsibleBranchLine
      key={stringifyKey(branchKey)}
      branchKey={branchKey}
      renderWhenVisible={renderWhenVisible}
      className={classNames(nonBuilt && withTitle === true && styles.nonBuilt)}
      isVisible={isVisible}
    />
  )

  const title = nonBuilt
    ? `${branches.length} Non-built ${plur('Branch', branches.length)}`
    : `${branches.length} ${plur('Branch', branches.length)} with builds`
  const header = React.useMemo(
    () =>
      withTitle === true ? (
        <div className={styles.header}>
          <H3 className={styles.title} key={title}>
            {title}
          </H3>
          <SvgIcon className={styles.arrow} icon={expanded ? 'chevron-down' : 'chevron-right'} />
        </div>
      ) : null,
    [expanded, withTitle, title],
  )
  const handleToggle = React.useCallback(() => {
    expanded ? onCollapse(buildTypeId, branchesToShow) : onExpand(buildTypeId, branchesToShow)
  }, [buildTypeId, branchesToShow, onCollapse, onExpand, expanded])
  const gridClassName = classNames(styles.container, useBuildsClassName())

  if (branches.length === 0) {
    return null
  }

  return (
    <Details
      level={2}
      imitateHeading
      summaryClassName={classNames(styles.line, withTitle !== true && styles.headless)}
      summary={header}
      label={title}
      onToggle={handleToggle}
      open={expanded}
    >
      <Incremental initialCount={IMMEDIATE_RENDER_COUNT} cooldown={300}>
        {groups.map((group, i) =>
          renderWhenVisible ? ( // eslint-disable-next-line react/no-array-index-key
            <Visible key={i}>
              {isVisible => (
                <Grid className={gridClassName}>
                  {group.map(branch => generateBranchItem(branch, isVisible))}
                </Grid>
              )}
            </Visible>
          ) : (
            // eslint-disable-next-line react/no-array-index-key
            <Grid key={i} className={gridClassName}>
              {group.map(branch => generateBranchItem(branch))}
            </Grid>
          ),
        )}
      </Incremental>
    </Details>
  )
}
